import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, GridOptions, ICellRendererParams, IRowNode, ValueFormatterParams } from 'ag-grid-community';

import { ItemListCell, TextButtonCell } from '~/components/common';

import { maskName } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT } from '~/constants';
import { CLAIM_RETURN_STATUS_BADGE_COLOR, type TypeClaimReturnStatus } from '~/constants/claim';

import type { TypeReturnData } from '~/types/claim.types';

/**
 * [반품 신청/조회] 페이지 > 테이블 컬럼 정의
 */
const getClaimReturnColumnDefs = ({
  hasPermission3PL,
  onClickReturnReceiptId,
}: {
  hasPermission3PL: boolean;
  onClickReturnReceiptId: (returnReceiptId: string) => void;
}) => {
  const gridOptions: GridOptions = {
    rowSelection: 'single',
  };

  const columnDefs: ColDef<TypeReturnData>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      width: 72,
      checkboxSelection: hasPermission3PL,
    },
    {
      field: 'status',
      headerName: '반품상태',
      cellDataType: false,
      width: 100,
      cellRenderer: Badge,
      cellRendererParams: ({ value }: ICellRendererParams<TypeReturnData, TypeReturnData['status']>) => ({
        variant: 'light',
        status: true,
        color: CLAIM_RETURN_STATUS_BADGE_COLOR[value?.code as TypeClaimReturnStatus],
        children: value?.name,
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'returnReceiptAt',
      headerName: '반품접수일시',
      width: 160,
      valueFormatter: ({ value }: ValueFormatterParams<TypeReturnData, TypeReturnData['returnReceiptAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      field: 'clientOrderCode',
      headerName: '원판매처 주문번호',
      width: 180,
    },
    {
      field: 'returnReceiptId',
      headerName: '반품요청번호',
      width: 180,
      cellRenderer: TextButtonCell,
      cellRendererParams: ({ data }: ICellRendererParams<TypeReturnData, TypeReturnData['returnReceiptId']>) => ({
        children: data?.returnReceiptId ?? '-',
        onClick: ({ data }: IRowNode<TypeReturnData>) => {
          if (!data) return;
          onClickReturnReceiptId(data.returnReceiptId);
        },
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'member.name',
      headerName: '수령자명',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeReturnData, TypeReturnData['member']['name']>) =>
        maskName(value) ?? '-',
    },
    {
      field: 'returnCourier.name',
      headerName: '회수 배송사',
      width: 88,
    },
    {
      field: 'returnOrderTrackings',
      headerName: '회수 송장번호',
      minWidth: 280,
      flex: 1,
      valueFormatter: ({ value }: ValueFormatterParams<TypeReturnData, TypeReturnData['returnOrderTrackings']>) => {
        if (!value || value.length === 0) return '-';
        return value.length > 1 ? `${value[0].invoiceNumber} 외 ${value.length - 1}개` : value[0].invoiceNumber;
      },
    },
    {
      field: 'outboundOrderCode',
      headerName: '원출고요청번호',
      width: 180,
    },
    {
      headerName: '반품 상품명',
      minWidth: 90,
      width: 240,
      cellRenderer: ItemListCell,
      cellRendererParams: ({ data }: ICellRendererParams<TypeReturnData, TypeReturnData['returnOrderItems']>) => ({
        value: data?.returnOrderItems ?? [],
        fieldName: 'goodsName',
      }),
      cellStyle: { overflow: 'hidden', whiteSpace: 'normal' },
    },
    {
      field: 'updatedAt',
      headerName: '최근 업데이트일시',
      width: 159,
      valueFormatter: ({ value }: ValueFormatterParams<TypeReturnData, TypeReturnData['updatedAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { gridOptions, columnDefs };
};

export default getClaimReturnColumnDefs;
